import React, { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, Link2, Link2Off, RefreshCw, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import api from '../lib/api';

const RETURN_MSG = {
  connected: ['success', 'Google Agenda connecté'],
  refused: ['error', "Autorisation Google refusée ou annulée"],
  error: ['error', "La connexion Google a échoué (aucun jeton valide reçu)"],
};

export const GoogleCalendarCard = ({ onSynced }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.get('/admin/google-calendar/status').then((r) => setSt(r.data)).catch(() => setSt(null)), []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const msg = RETURN_MSG[p.get('gcal')];
    if (msg) {
      toast[msg[0]](msg[1]);
      window.history.replaceState({}, '', window.location.pathname);
    }
    load();
  }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || 'Opération impossible'); }
    setBusy('');
    load();
  };
  const connect = () => run('connect', async () => {
    const r = await api.post('/admin/google-calendar/connect', { origin: window.location.origin });
    window.location.href = r.data.authorization_url;
  });
  const test = () => run('test', async () => { await api.post('/admin/google-calendar/test'); toast.success('Connexion Google Agenda vérifiée'); });
  const resync = () => run('resync', async () => {
    const r = await api.post('/admin/google-calendar/resync');
    const { processed, failed } = r.data;
    (failed ? toast.error : toast.success)(`${processed} traité(s), ${failed} en échec`);
    onSynced?.();
  });
  const disconnect = () => run('disconnect', async () => { await api.post('/admin/google-calendar/disconnect'); toast.success('Google Agenda déconnecté'); });

  if (!st) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid="gcal-card">
      <CalendarCheck className={`w-5 h-5 ${st.connected ? 'text-emerald-600' : 'text-slate-400'}`} />
      <div className="text-sm mr-auto">
        <p className="font-semibold text-slate-900" data-testid="gcal-status">
          Google Agenda : {!st.configured ? 'identifiants OAuth non configurés' : st.connected ? 'connecté' : 'non connecté'}
        </p>
        <p className="text-xs text-slate-500">
          {st.auth_error && <span className="text-red-600" data-testid="gcal-auth-error">{st.auth_error} · </span>}
          {st.connected && st.connected_at && `Depuis le ${new Date(st.connected_at).toLocaleDateString('fr-FR')} · `}
          <span data-testid="gcal-to-sync">{st.to_sync} rendez-vous à synchroniser</span>
        </p>
      </div>
      {st.configured && !st.connected && (
        <Button size="sm" onClick={connect} disabled={!!busy} data-testid="gcal-connect-btn"><Link2 className="w-4 h-4 mr-1" />Connecter Google Agenda</Button>
      )}
      {st.connected && (
        <>
          <Button size="sm" variant="outline" onClick={test} disabled={!!busy} data-testid="gcal-test-btn"><PlugZap className="w-4 h-4 mr-1" />Tester</Button>
          <Button size="sm" variant="outline" onClick={resync} disabled={!!busy} data-testid="gcal-resync-btn"><RefreshCw className={`w-4 h-4 mr-1 ${busy === 'resync' ? 'animate-spin' : ''}`} />Resynchroniser</Button>
          <Button size="sm" variant="ghost" onClick={disconnect} disabled={!!busy} data-testid="gcal-disconnect-btn"><Link2Off className="w-4 h-4 mr-1" />Déconnecter</Button>
        </>
      )}
    </div>
  );
};
